/**
 * Global Alarm Scheduler
 * --------------------------------------------------
 * Runs on every page after login. Polls SuperTool tasks
 * with alarm_enabled=true and schedules precise setTimeout
 * triggers so the alarm fires exactly at:
 *     (due_date + " " + due_time) - alarm_minutes_before
 *
 * When fired, plays a sustained Web Audio API tone for ~10s
 * and shows a fullscreen modal with Dismiss/Snooze actions.
 *
 * Uses localStorage `firedAlarms` to prevent re-firing on
 * page reload/navigation.
 *
 * Note: setTimeout is used (NOT setInterval) because:
 *   - it triggers at the exact millisecond
 *   - setInterval polling drifts and can be late by 30+ seconds
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Clock, AlarmClock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// localStorage key for fired alarms
const FIRED_KEY = 'supertool_fired_alarms_v1';

// How often we re-fetch task list (in ms). 60s is enough since we
// then use precise setTimeout per task, not polling.
const REFRESH_INTERVAL_MS = 60 * 1000;

// Tone frequencies — match the SuperTool preview map
const TONE_FREQUENCIES = {
  gentle_morning: 440, temple_bells: 523, crystal_chime: 659,
  zen_garden: 392, morning_birds: 587, soft_piano: 349,
  flute_melody: 494, water_drops: 415, wind_chimes: 554,
  om_chant: 261, sitar_tune: 466, tabla_beat: 311,
  conch_shell: 233, aarti_bells: 622, classic_alarm: 880,
  digital_beep: 1000, notification_pop: 700, success_chime: 784,
  marimba: 523, harp_gliss: 698
};

const TONE_WAVES = {
  // soft / spiritual -> sine/triangle, standard -> square
  gentle_morning: 'sine', temple_bells: 'triangle', crystal_chime: 'sine',
  zen_garden: 'sine', morning_birds: 'sine', soft_piano: 'sine',
  flute_melody: 'sine', water_drops: 'sine', wind_chimes: 'sine',
  om_chant: 'triangle', sitar_tune: 'triangle', tabla_beat: 'square',
  conch_shell: 'triangle', aarti_bells: 'triangle', classic_alarm: 'square',
  digital_beep: 'square', notification_pop: 'sine', success_chime: 'sine',
  marimba: 'triangle', harp_gliss: 'sine'
};

// ---------- localStorage helpers ----------
const loadFired = () => {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const saveFired = (obj) => {
  try {
    localStorage.setItem(FIRED_KEY, JSON.stringify(obj));
  } catch (e) {
    // localStorage may be unavailable in private mode
  }
};

const pruneFired = () => {
  // Prune fired entries older than 48h to keep the file small
  const fired = loadFired();
  const now = Date.now();
  const fresh = {};
  Object.entries(fired).forEach(([k, ts]) => {
    if (now - ts < 48 * 3600 * 1000) fresh[k] = ts;
  });
  saveFired(fresh);
  return fresh;
};

// ---------- Time computation ----------
/**
 * Returns the alarm trigger time in ms (browser local epoch).
 * Browser is expected to be in IST for Indian users; we treat
 * due_date + due_time as local IST clock time.
 */
const computeTriggerMs = (task) => {
  if (!task.due_date || !task.due_time) return null;
  const minutesBefore = parseInt(task.alarm_minutes_before || 0, 10);
  // ISO "YYYY-MM-DDTHH:MM" — interpreted as browser local time
  const dt = new Date(`${task.due_date}T${task.due_time}`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.getTime() - minutesBefore * 60 * 1000;
};

const alarmKey = (task) =>
  `${task.id}_${task.due_date || ''}_${task.due_time || ''}_${task.alarm_minutes_before || 0}`;

// ---------- Web Audio API alarm player (sustained ~10s) ----------
class AlarmPlayer {
  constructor() {
    this.ctx = null;
    this.activeNodes = [];
    this.stopTimer = null;
  }

  start(toneId) {
    this.stop();
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      // Some browsers leave context suspended until user gesture.
      // We attempt to resume immediately.
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {});
      }
      const freq = TONE_FREQUENCIES[toneId] || 880;
      const wave = TONE_WAVES[toneId] || 'square';

      // Pattern: 0.5s on, 0.25s off, 0.5s on, 0.25s off ... for ~10s
      const totalMs = 10000;
      const onMs = 500;
      const offMs = 250;
      const cycleMs = onMs + offMs;
      const now = this.ctx.currentTime;
      const totalCycles = Math.ceil(totalMs / cycleMs);

      for (let i = 0; i < totalCycles; i++) {
        const start = now + (i * cycleMs) / 1000;
        const end = start + onMs / 1000;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = wave;
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.4, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, end);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(start);
        osc.stop(end + 0.05);
        this.activeNodes.push(osc);
      }

      this.stopTimer = setTimeout(() => this.stop(), totalMs + 200);
    } catch (err) {
      console.error('AlarmPlayer.start failed:', err);
    }
  }

  startCustomUrl(url) {
    this.stop();
    try {
      const audio = new Audio(url);
      audio.loop = true;
      audio.volume = 0.9;
      audio.play().catch((e) => console.warn('custom alarm play blocked:', e));
      this.htmlAudio = audio;
      // Auto-stop after 15s
      this.stopTimer = setTimeout(() => this.stop(), 15000);
    } catch (err) {
      console.error('AlarmPlayer.startCustomUrl failed:', err);
    }
  }

  stop() {
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    this.activeNodes.forEach((o) => {
      try { o.stop(); } catch (e) { /* already stopped */ }
      try { o.disconnect(); } catch (e) { /* already disconnected */ }
    });
    this.activeNodes = [];
    if (this.htmlAudio) {
      try { this.htmlAudio.pause(); this.htmlAudio.src = ''; } catch (e) { /* noop */ }
      this.htmlAudio = null;
    }
    if (this.ctx) {
      try { this.ctx.close(); } catch (e) { /* already closed */ }
      this.ctx = null;
    }
  }
}

const AlarmScheduler = () => {
  const { user } = useAuth();
  const [active, setActive] = useState(null); // currently firing task (for modal)
  const playerRef = useRef(null);
  const timersRef = useRef(new Map()); // key -> timeoutId
  const refreshTimerRef = useRef(null);
  const firedRef = useRef(pruneFired());

  if (!playerRef.current) playerRef.current = new AlarmPlayer();

  // ---------- Fire an alarm ----------
  const fireAlarm = useCallback((task) => {
    const key = alarmKey(task);
    if (firedRef.current[key]) return;
    firedRef.current[key] = Date.now();
    saveFired(firedRef.current);

    // Play sustained tone
    if (task.custom_tone_url) {
      playerRef.current.startCustomUrl(task.custom_tone_url);
    } else {
      playerRef.current.start(task.alarm_tone || 'classic_alarm');
    }

    // Trigger browser notification if permitted (visual cue when tab inactive)
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('⏰ Reminder', {
          body: `${task.title}${task.due_time ? ' — ' + task.due_time : ''}`,
          tag: key,
          requireInteraction: true
        });
      }
    } catch (e) {
      // notifications blocked or not supported
    }

    setActive(task);
  }, []);

  // ---------- Schedule precise setTimeout for a task ----------
  const scheduleTask = useCallback((task) => {
    if (!task.alarm_enabled || task.is_completed) return;
    const key = alarmKey(task);
    if (firedRef.current[key]) return; // already fired

    // Clear any existing timer for this key
    const existing = timersRef.current.get(key);
    if (existing) clearTimeout(existing);

    const triggerMs = computeTriggerMs(task);
    if (triggerMs == null) return;

    const delay = triggerMs - Date.now();

    // If trigger is far in past (>5 min), skip (stale)
    if (delay < -5 * 60 * 1000) return;

    // If trigger is in the past but within 5 min window, fire immediately
    if (delay <= 0) {
      fireAlarm(task);
      return;
    }

    // Cap setTimeout to ~24 days; for longer delays, we'll catch
    // it in the next REFRESH_INTERVAL_MS poll.
    const cappedDelay = Math.min(delay, 24 * 24 * 3600 * 1000);

    const id = setTimeout(() => {
      fireAlarm(task);
      timersRef.current.delete(key);
    }, cappedDelay);
    timersRef.current.set(key, id);
  }, [fireAlarm]);

  // ---------- Fetch tasks and (re)schedule ----------
  const refreshTasks = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/supertool/tasks');
      const tasks = res.data || [];
      // Clear old timers — we'll re-schedule all current alarm-enabled tasks
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current.clear();

      tasks
        .filter((t) => t.alarm_enabled && !t.is_completed)
        .forEach((t) => scheduleTask(t));
    } catch (err) {
      // If supertool is disabled (403) or any other issue, just stop silently
      if (err?.response?.status !== 403) {
        console.warn('AlarmScheduler refresh failed:', err?.message);
      }
    }
  }, [user, scheduleTask]);

  // ---------- Setup polling on login ----------
  useEffect(() => {
    if (!user) return undefined;

    // Request notification permission once (silently — no toast)
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    refreshTasks();
    refreshTimerRef.current = setInterval(refreshTasks, REFRESH_INTERVAL_MS);

    // Allow manual "Test Alarm" button in SuperTool to trigger fire immediately
    const handleTestEvent = (e) => {
      if (e.detail && e.detail.id) {
        // Force-fire by clearing firedRef for this key
        const key = alarmKey(e.detail);
        delete firedRef.current[key];
        fireAlarm({ ...e.detail, _test: true });
      }
    };
    window.addEventListener('supertool:test-alarm', handleTestEvent);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      const timers = timersRef.current;
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      if (playerRef.current) playerRef.current.stop();
      window.removeEventListener('supertool:test-alarm', handleTestEvent);
    };
  }, [user, refreshTasks, fireAlarm]);

  // ---------- Dismiss / Snooze handlers ----------
  const handleDismiss = () => {
    if (playerRef.current) playerRef.current.stop();
    setActive(null);
  };

  const handleSnooze = (mins = 5) => {
    if (!active) return;
    if (playerRef.current) playerRef.current.stop();
    const key = alarmKey(active);
    // Remove from fired so it can fire again after snooze window
    delete firedRef.current[key];
    saveFired(firedRef.current);

    const task = active;
    setActive(null);
    const id = setTimeout(() => fireAlarm(task), mins * 60 * 1000);
    timersRef.current.set(key + '_snooze_' + Date.now(), id);
  };

  // Don't render anything if no active alarm
  if (!active) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="alarm-modal"
    >
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-8 max-w-md w-full shadow-2xl animate-pulse">
        <div className="flex flex-col items-center text-center">
          <div className="bg-white/20 rounded-full p-5 mb-4">
            <AlarmClock className="w-16 h-16 text-white" data-testid="alarm-icon" />
          </div>
          <div className="text-white/80 text-sm font-semibold uppercase tracking-wider mb-2">
            ⏰ Reminder
          </div>
          <h2 className="text-3xl font-bold text-white mb-2" data-testid="alarm-title">
            {active.title}
          </h2>
          {active.description && (
            <p className="text-white/90 text-sm mb-4">{active.description}</p>
          )}
          <div className="flex items-center gap-2 text-white/90 mb-6">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">
              {active.due_date} {active.due_time && `at ${active.due_time}`}
            </span>
          </div>

          <div className="flex gap-3 w-full">
            <button
              onClick={() => handleSnooze(5)}
              className="flex-1 px-4 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition"
              data-testid="alarm-snooze-5min-btn"
            >
              Snooze 5m
            </button>
            <button
              onClick={() => handleSnooze(10)}
              className="flex-1 px-4 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition"
              data-testid="alarm-snooze-10min-btn"
            >
              Snooze 10m
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-3 bg-white text-orange-600 font-bold rounded-xl hover:bg-white/90 transition flex items-center justify-center gap-2"
              data-testid="alarm-dismiss-btn"
            >
              <X className="w-4 h-4" /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlarmScheduler;
